import * as yup from 'yup'
import { defineMessages, IntlShape } from 'react-intl'
import { IPeakSettingsFormValues, PeakSettingsValue } from 'features/Services/components/PeakSettings/types'
import { isAfter, isBefore, startOfDay } from 'date-fns'

export const messages = defineMessages({
  dateRangeError: {
    id: 'services.peakSettings.dateValidation',
    defaultMessage: 'Date range must not overlap with previous range.'
  },
  required: {
    id: 'services.peakSettings.required',
    defaultMessage: 'Enter valid range'
  },
  offerDateValidation: {
    id: 'services.peakSettings.offerDateValidation',
    defaultMessage: 'Date must be within the offer settings range'
  }
})

const validateConsecutiveDate = (
  currentValue: PeakSettingsValue,
  previousValue: PeakSettingsValue | undefined
): boolean => {
  const prevEndAtDate = previousValue?.endAt
  const currentStartDate = currentValue.startAt
  if (prevEndAtDate && currentStartDate) {
    return prevEndAtDate < currentStartDate
  }
  return true
}

const validateRequiredDate = (value: PeakSettingsValue): boolean => {
  const currentEndDate = value.endAt
  const currentStartDate = value.startAt
  if (!currentEndDate && !currentStartDate) {
    return true
  }
  return Boolean(currentEndDate && currentStartDate && currentEndDate >= currentStartDate)
}

const validateDateInRange = (
  value: Date,
  range: {
    from?: Date
    to?: Date
  }
): boolean => {
  const isInRangeFrom = range.from && !isBefore(value, range.from)
  const isValidFrom = Boolean(!range.from || isInRangeFrom)
  const isInRangeTo = range.to && !isAfter(value, range.to)
  const isValidTo = Boolean(!range.to || isInRangeTo)

  return isValidFrom && isValidTo
}

export const getPeakSettingsSchema = (intl: IntlShape): yup.SchemaOf<IPeakSettingsFormValues> => {
  return yup.object().shape({
    peakSettings: yup.array().of(
      yup
        .object()
        .shape({
          id: yup.string(),
          startAt: yup.date(),
          endAt: yup.date()
        })
        .test('validate-consecutive-date', intl.formatMessage(messages.dateRangeError), function (value) {
          const index = this.parent.indexOf(value)
          const previousValue = index > 0 ? this.parent[index - 1] : undefined
          return validateConsecutiveDate(value, previousValue)
        })
        .test('validate-required-date', intl.formatMessage(messages.required), validateRequiredDate)
        .test('validate-offer-settings-date', intl.formatMessage(messages.offerDateValidation), function (value) {
          const offerStartDate = this.options.context?.startDate
          const offerEndDate = this.options.context?.endDate

          if (!value.startAt && !value.endAt) {
            return true
          }

          const isStartInRange =
            value.startAt &&
            validateDateInRange(startOfDay(value.startAt), {
              from: offerStartDate && startOfDay(offerStartDate),
              to: offerEndDate && startOfDay(offerEndDate)
            })
          const isValidStartDate = Boolean(!value.startAt || isStartInRange)

          const isEndInRange =
            value.endAt &&
            validateDateInRange(startOfDay(value.endAt), {
              from: offerStartDate && startOfDay(offerStartDate),
              to: offerEndDate && startOfDay(offerEndDate)
            })
          const isValidEndDate = Boolean(!value.endAt || isEndInRange)

          return isValidStartDate && isValidEndDate
        })
    )
  })
}

export { validateConsecutiveDate, validateRequiredDate, validateDateInRange }
